// Контракт API пульта (задача D8): список диалогов — исходы, причины передачи, флаги проблем, запрос списка, строка списка,
// ответ, соседи и поиск по номеру.
//
// `GET /api/v1/dialogs` (любой вошедший) → `DialogListResponse`: страница строк, `counts` по всем сегментам (при тех же
// остальных фильтрах) и курс, по которому доллары расхода пересчитаны в рубли. `GET /api/v1/dialogs/lookup?q=48213` →
// `{ dialogId }`, `GET /api/v1/dialogs/:id/neighbors?{фильтры списка}` → `Neighbors` («‹ 12 из 148 ›» и клавиши J и K).
//
// Правило контракта: файл импортирует только `zod` и соседей (`./*.js`), типы — `z.infer`. Перечисления исходов и флагов —
// копии кода витрины (`src/console/dialogs/flags.ts`); совпадение держит `tests/console/dialog-flags.test.ts`.
import { z } from 'zod';
import { FxSchema, ModeSchema, MomentSchema, type StatusTone } from './common.js';

// ───────────────────────────── исход диалога ─────────────────────────────

/**
 * Чем кончился диалог: в продажи, отказ, к человеку, к менеджеру сделки, не клиент, завершён, ведёт, ошибка. Первое
 * совпадение из правил витрины: упал последний ход → `error`; итог-действие (`lead_to_sales`, `lead_reject`,
 * `human_handoff`); решение последнего хода (`redirect_not_client`, `close_polite`); иначе `in_progress`.
 */
export const OUTCOMES = ['to_sales', 'reject', 'handoff', 'repeat_client', 'not_client', 'closed', 'in_progress', 'error'] as const;
export const OutcomeSchema = z.enum(OUTCOMES);
export type Outcome = z.infer<typeof OutcomeSchema>;

/** Исход словами — подпись рядом с точкой статуса. */
export const OUTCOME_RU: Readonly<Record<Outcome, string>> = {
  to_sales: 'В продажи',
  reject: 'Отказ',
  handoff: 'К человеку',
  repeat_client: 'К менеджеру сделки',
  not_client: 'Не клиент',
  closed: 'Завершён',
  in_progress: 'Ведёт',
  error: 'Ошибка',
};

/** Цвет точки исхода: зелёная — в продажи, серая — отказ и закрытое, янтарная — к человеку, синяя — ведёт, красная — ошибка. */
export const OUTCOME_TONE: Readonly<Record<Outcome, StatusTone>> = {
  to_sales: 'sales',
  reject: 'reject',
  handoff: 'human',
  repeat_client: 'human',
  not_client: 'reject',
  closed: 'reject',
  in_progress: 'live',
  error: 'error',
};

// ───────────────────────────── причина передачи ─────────────────────────────

/**
 * Причины передачи человеку (`actions.payload.reason` у `human_handoff`) и их подписи. Первые двенадцать — по таблице
 * брифа D8; `disputed_reject` — причина A11 (клиенту уже отказали, а он пишет снова, Ruling 65), которой в таблице брифа
 * нет: без строки у такой передачи не было бы подписи; `call_request` — звонок по номеру (P1, Ruling 121): номер клиента
 * у менеджера, исход «К человеку»; `reject_review` — отказ на проверку человеку при `qualify.reject = handoff` (ревью
 * волны 1, I2); `not_qualified` — ЦА не подтверждена, а номера от клиента нет: чат квалификатору (план A23); `no_phone` — ЦА
 * подтверждена, а номера нет: отказался, не ответил или замолчал — чат квалификатору (план PH). Код, которого
 * нет в таблице (его записал более новый воркер), в строке списка получает подпись «другое».
 */
export const HANDOFF_REASON_RU = {
  wants_manager: 'просит менеджера',
  wants_call: 'просит звонок',
  irritated: 'раздражён',
  complaint: 'жалоба',
  legal: 'юридический вопрос',
  not_understood: 'не поняла клиента',
  out_of_kb: 'нет ответа в базе',
  disputed_product: 'спорный товар',
  budget: 'бюджет исчерпан',
  model_error: 'сбой модели',
  check_failed: 'ответ не прошёл проверку',
  repeat_client: 'повторный клиент',
  other: 'другое',
  disputed_reject: 'клиент спорит с отказом',
  call_request: 'звонок по номеру клиента',
  reject_review: 'отказ на проверку',
  not_qualified: 'ЦА не определена',
  no_phone: 'ЦА без номера',
  fulfillment: 'Фулфилмент',
  // План MAIL, A2: письмо Анны не дошло до клиента (`delivery_failed` у `ai-reply`). Подпись — ровно та строка, которую Омнидеск
  // сопоставляет в очереди (`reasonRu` у `ai-qual-state`, contracts.md плана MAIL), поэтому с большой буквы и без правок.
  mail_undelivered: 'Письмо не дошло',
} as const satisfies Readonly<Record<string, string>>;
export type HandoffReason = keyof typeof HANDOFF_REASON_RU;

// ───────────────────────────── флаги проблем ─────────────────────────────

/**
 * Все флаги диалога в витрине (`console_dialogs.flags`). `fabricated` и `false_reject` ставит разбор человека (D11),
 * `handoff_late` — «передача без ответа в срок» — ставят ворота «Обзора» (D13: им нужно время ответа человека, которого
 * в своей базе нет); `first_reply_late`, `robot` и `client_silent` — «клиент замолчал после напоминаний» (план N, N2) —
 * признаки, но не «проблемы» (см. `PROBLEM_FLAGS`).
 */
export const FLAG_CODES = [
  'failed',
  'fabricated',
  'double',
  'neutral',
  'judge_no',
  'unsupported',
  'false_reject',
  'complaint',
  'caught',
  'robot',
  'first_reply_late',
  'handoff_late',
  'client_silent',
] as const;
export const ProblemFlagSchema = z.enum(FLAG_CODES);
export type ProblemFlag = z.infer<typeof ProblemFlagSchema>;

/** Флаг словами: из первого по важности `problemRu` строки списка и подписи в разборе. */
export const PROBLEM_FLAG_RU: Readonly<Record<ProblemFlag, string>> = {
  failed: 'ход упал',
  fabricated: 'выдумала факт (разбор)',
  double: 'двойной ответ',
  neutral: 'два варианта не прошли проверку',
  judge_no: 'оценщик: не ответила на вопрос',
  unsupported: 'оценщик: утверждение без опоры',
  false_reject: 'ложный отказ (разбор)',
  complaint: 'жалоба клиента',
  caught: 'поймано проверкой',
  robot: 'спросил про робота и раздражён',
  first_reply_late: 'первый ответ позже 5 минут',
  handoff_late: 'передача без ответа в срок',
  client_silent: 'клиент замолчал после напоминаний',
};

/**
 * Флаги сегмента «Проблемные» («проверка не пройдена, оценщик сказал «нет», жалоба или ошибка») — в порядке важности:
 * по первому из них строка списка пишет `problemRu`. Диалог «проблемный» (`console_dialogs.problem`), если есть хотя бы
 * один из них.
 */
export const PROBLEM_FLAGS = [
  'failed',
  'fabricated',
  'double',
  'neutral',
  'judge_no',
  'unsupported',
  'false_reject',
  'complaint',
  'caught',
] as const satisfies readonly ProblemFlag[];

// ───────────────────────────── запрос списка ─────────────────────────────

/** Сегменты списка: все, не просмотренные мной, передачи, отказы, проблемные. */
export const SEGMENTS = ['all', 'unviewed', 'handoffs', 'rejects', 'problems'] as const;
export const SegmentSchema = z.enum(SEGMENTS);
export type Segment = z.infer<typeof SegmentSchema>;

/** Фильтр по оценщику: есть «нет», все «да», оценщик не проверял. */
export const GRADE_FILTERS = ['has_no', 'all_yes', 'none'] as const;

/** Период: сегодня (с 00:00 МСК), 7 и 30 дней (включая сегодня, по дням МСК), всё время. */
export const PERIODS = ['today', '7d', '30d', 'all'] as const;

/** Ворота «Обзора» (D13), по которым открывают список: ворота → флаг витрины. */
export const GATE_FLAG = {
  answered: 'judge_no',
  fabricated: 'fabricated',
  double_replies: 'double',
  first_reply_5min: 'first_reply_late',
  false_rejects: 'false_reject',
  robot_complaints: 'robot',
  handoff_in_time: 'handoff_late',
} as const satisfies Readonly<Record<string, ProblemFlag>>;
export const GATES = ['answered', 'fabricated', 'double_replies', 'first_reply_5min', 'false_rejects', 'robot_complaints', 'handoff_in_time'] as const;

/** Порядок списка: новые сверху, старые сверху, дорогие сверху. */
export const SORTS = ['started_desc', 'started_asc', 'cost_desc'] as const;

/** Сколько диалогов на странице: умолчание и предел. */
export const DIALOG_PAGE_SIZE = 50;
export const DIALOG_PAGE_SIZE_MAX = 100;

const CHANNEL_KEY = /^[a-z0-9_:.-]{1,64}$/;

/** Текст ошибки «не из списка»: параметр и допустимые значения. */
function oneOf(name: string, values: readonly string[]): { error: string } {
  return { error: `${name} — одно из: ${values.join(', ')}.` };
}

/**
 * Параметры списка из строки запроса (`GET /dialogs`, `…/neighbors`). Текст поиска `q` — 1…100 знаков; период — `period`
 * (по умолчанию 7 дней) или точные `from` и `to` (ISO, обе границы входят), они заменяют `period`.
 */
export const DialogListQuerySchema = z.object({
  segment: z.enum(SEGMENTS, oneOf('segment', SEGMENTS)).default('all'),
  q: z
    .string({ error: 'q — текст поиска: строка.' })
    .min(1, 'q — текст поиска: не меньше одного знака.')
    .max(100, 'q — текст поиска: не больше 100 знаков.')
    .optional(),
  channel: z.string({ error: 'channel — ключ канала.' }).regex(CHANNEL_KEY, 'channel — ключ канала вроде avito или textback:whatsappb: строчные латинские буквы, цифры и знаки _ : . -').optional(),
  outcome: z.enum(OUTCOMES, oneOf('outcome', OUTCOMES)).optional(),
  grades: z.enum(GRADE_FILTERS, oneOf('grades', GRADE_FILTERS)).optional(),
  mode: ModeSchema.optional(),
  period: z.enum(PERIODS, oneOf('period', PERIODS)).default('7d'),
  from: z.iso.datetime({ error: 'from — момент ISO вроде 2026-09-29T00:00:00Z.' }).optional(),
  to: z.iso.datetime({ error: 'to — момент ISO вроде 2026-10-05T23:59:59Z.' }).optional(),
  gate: z.enum(GATES, oneOf('gate', GATES)).optional(),
  sort: z.enum(SORTS, oneOf('sort', SORTS)).default('started_desc'),
  cursor: z.string({ error: 'cursor — строка из ответа списка.' }).max(300, 'cursor — строка из ответа списка.').optional(),
  limit: z.coerce
    .number({ error: `limit — сколько диалогов на странице: целое число от 1 до ${String(DIALOG_PAGE_SIZE_MAX)}.` })
    .int(`limit — сколько диалогов на странице: целое число от 1 до ${String(DIALOG_PAGE_SIZE_MAX)}.`)
    .min(1, `limit — сколько диалогов на странице: целое число от 1 до ${String(DIALOG_PAGE_SIZE_MAX)}.`)
    .max(DIALOG_PAGE_SIZE_MAX, `limit — сколько диалогов на странице: целое число от 1 до ${String(DIALOG_PAGE_SIZE_MAX)}.`)
    .default(DIALOG_PAGE_SIZE),
});
export type DialogListQuery = z.infer<typeof DialogListQuerySchema>;

// ───────────────────────────── ответы ─────────────────────────────

/**
 * Строка списка. Клиент — коротко («Иван П.», номер вместо имени — маской), канал словами; рубли — расход в долларах по
 * курсу ЦБ на сегодня; `grades` — сколько проверок оценщика ответили «да» из скольких; `viewed` — просмотрен ли этот
 * диалог мной после последней активности; `problemRu` — первая по важности проблема (`null` — проблемы нет).
 */
export const DialogRowSchema = z.object({
  id: z.string().min(1),
  ticketNumber: z.number().int().positive().nullable(),
  customerLabel: z.string().min(1),
  channelLabel: z.string().min(1),
  startedAt: MomentSchema,
  lastActivityAt: MomentSchema,
  outcome: OutcomeSchema,
  handoffReasonRu: z.string().nullable(),
  turns: z.number().int().nonnegative(),
  firstReplySec: z.number().int().nonnegative().nullable(),
  costRub: z.number().nonnegative(),
  grades: z.object({ yes: z.number().int().nonnegative(), total: z.number().int().nonnegative() }),
  mode: ModeSchema,
  viewed: z.boolean(),
  problem: z.boolean(),
  problemRu: z.string().nullable(),
});
export type DialogRow = z.infer<typeof DialogRowSchema>;

/** Сколько диалогов в каждом сегменте при тех же остальных фильтрах. */
export const SegmentCountsSchema = z.object({
  all: z.number().int().nonnegative(),
  unviewed: z.number().int().nonnegative(),
  handoffs: z.number().int().nonnegative(),
  rejects: z.number().int().nonnegative(),
  problems: z.number().int().nonnegative(),
});
export type SegmentCounts = z.infer<typeof SegmentCountsSchema>;

/**
 * Страница списка: `total` — диалогов в выбранном сегменте (всех страниц), `nextCursor` — курсор следующей страницы
 * (`null` — страниц больше нет), `at` — момент ответа, `fx` — курс, по которому посчитаны рубли.
 */
export const DialogListResponseSchema = z.object({
  items: z.array(DialogRowSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int().nonnegative(),
  counts: SegmentCountsSchema,
  at: MomentSchema,
  fx: FxSchema,
});
export type DialogListResponse = z.infer<typeof DialogListResponseSchema>;

/**
 * Соседи диалога в выборке списка: `index` — место диалога (с единицы; `null` — диалога нет в выборке), `total` — размер
 * выборки, `prevId` и `nextId` — соседние диалоги в порядке списка (`null` — края).
 */
export const NeighborsSchema = z.object({
  index: z.number().int().positive().nullable(),
  total: z.number().int().nonnegative(),
  prevId: z.string().nullable(),
  nextId: z.string().nullable(),
});
export type Neighbors = z.infer<typeof NeighborsSchema>;

/** Ответ поиска по номеру тикета. */
export const LookupSchema = z.object({ dialogId: z.string().min(1) });
export type Lookup = z.infer<typeof LookupSchema>;

/** Чем искать диалог: номер тикета, который видят люди, или id чата Омнидеска (из ссылки `…/chats?id=…`). */
export type TicketRef = { readonly kind: 'number'; readonly number: number } | { readonly kind: 'omni'; readonly id: string };

/** Id чата Омнидеска (cuid): строчные латинские буквы и цифры, начинается с «c». */
const OMNI_CHAT_ID = /^c[a-z0-9]{20,40}$/u;
/** Параметр `id` ссылки на чат Омнидеска: `https://om.chtoday.ru/chats?id=cmv…`. */
const OMNI_CHAT_ID_PARAM = /[?&]id=(c[a-z0-9]{20,40})(?:[&#]|$)/u;

/**
 * Что ввели в поиск пульта (Ctrl+K, «Диалоги»; план PH, 20.10.2026: руководитель прислал ссылку на чат, а пульт искал только
 * по номеру): «48213» или «#48213» — номер тикета; ссылка на чат Омнидеска или сам id чата — `omni`; иначе `null` (поиск по
 * имени и тексту). Чистая функция — её берут и сервер, и пульт.
 */
export function ticketRefOf(text: string): TicketRef | null {
  const trimmed = text.trim();
  const digits = /^#?\s*(\d{1,9})$/u.exec(trimmed)?.[1];
  if (digits !== undefined) return Number(digits) >= 1 ? { kind: 'number', number: Number(digits) } : null;
  const id = OMNI_CHAT_ID_PARAM.exec(trimmed)?.[1] ?? (OMNI_CHAT_ID.test(trimmed) ? trimmed : undefined);
  return id === undefined ? null : { kind: 'omni', id };
}
